// Rules of thumb for the selectable factors.
//
// Design stance: SILENT BY DEFAULT. A first-time user shouldn't wade through
// a caption under every control explaining what they already accepted. These
// functions return null while a choice sits in its sensible range, and a
// short two-line tip only when the user has moved somewhere the engine would
// not have gone. The tip always says the rule of thumb and what it costs, so
// it teaches rather than scolds - and it never blocks the choice.
//
// This is the same restraint principle as style.ts (threshold-gated insights)
// and confidence.ts (de-dramatize low-stakes forks), applied to the controls.

import type { RoomType } from "./types.js";
import { ROOM_LIGHTING } from "./lighting.js";
import { sheenStep, type Sheen } from "./color.js";

export interface Tip {
  level: "info" | "caution";
  text: string;
}

const ROOM_WORD: Record<RoomType, string> = {
  kitchen: "kitchens", bath: "baths", bedroom: "bedrooms", living: "living rooms",
  dining: "dining rooms", laundry: "laundry rooms", office: "offices", other: "this room",
};

// --- Light color temperature -------------------------------------------------

export function cctTip(cct: number, roomType: RoomType): Tip | null {
  const profile = ROOM_LIGHTING[roomType] ?? ROOM_LIGHTING.other;
  const [lo, hi] = profile.cctBand;
  if (cct >= lo && cct <= hi) return null;

  const rest = roomType === "bedroom" || roomType === "living" || roomType === "dining";
  if (cct > hi) {
    return {
      level: "caution",
      text: rest
        ? `Rule of thumb: ${ROOM_WORD[roomType]} want 2700-3000K. Cooler light reads office-like and works against relaxing in the evening.`
        : `Cooler than the ${lo}-${hi}K this room usually wants. Crisp for task work, but it can feel clinical.`,
    };
  }
  return {
    level: "caution",
    text: roomType === "kitchen" || roomType === "bath" || roomType === "office"
      ? `Rule of thumb: ${ROOM_WORD[roomType]} want ${lo}K or cooler so food, faces and finishes stay true. Warmer light muddies color judgment.`
      : `Warmer than the ${lo}-${hi}K range for this room. Cozy, but detail and color accuracy suffer.`,
  };
}

// --- Brightness ---------------------------------------------------------------

export function brightnessTip(fc: number, recommended: number, roomType: RoomType): Tip | null {
  const ratio = fc / recommended;
  if (ratio >= 0.7 && ratio <= 1.4) return null;
  if (ratio > 1.4) {
    return {
      level: "caution",
      text: `That's well above the ~${recommended} fc ${ROOM_WORD[roomType]} usually need. Put it on a dimmer, or the room reads flat and over-lit at night.`,
    };
  }
  return {
    level: "caution",
    text: `Below the ~${recommended} fc ${ROOM_WORD[roomType]} usually need. Fine for mood, but add task lighting where people actually work.`,
  };
}

// --- Fixture count ------------------------------------------------------------

export function fixtureTip(count: number, recommended: number): Tip | null {
  if (recommended === 0) return null;
  const ratio = count / recommended;
  if (ratio >= 0.6 && ratio <= 1.5) return null;
  if (ratio < 0.6) {
    return {
      level: "caution",
      text: `Fewer cans means each works harder, which pools light and leaves dark corners. Wider spacing wants brighter lamps or extra floor lamps.`,
    };
  }
  return {
    level: "caution",
    text: `More fixtures than the spacing rule calls for. Tight grids can read like a runway; consider fewer cans on a dimmer instead.`,
  };
}

// --- Wall LRV -----------------------------------------------------------------

export function lrvTip(lrv: number, lrvFloor: number, roomType: RoomType): Tip | null {
  if (lrvFloor > 0 && lrv < lrvFloor) {
    return {
      level: "caution",
      text: `Rule of thumb: keep LRV above ${lrvFloor} in a room with this much light. Darker is a real look, but expect a cave-like feel and plan more lamps.`,
    };
  }
  if (lrv > 88 && (roomType === "living" || roomType === "bedroom" || roomType === "dining")) {
    return {
      level: "info",
      text: `Very high LRV can read stark in a living space. A few points down usually keeps the brightness but adds warmth.`,
    };
  }
  return null;
}

// --- Wall sheen ---------------------------------------------------------------

export function sheenTip(sheen: Sheen, roomType: RoomType, belowWetFloor: boolean): Tip | null {
  if (belowWetFloor) {
    return {
      level: "caution",
      text: `Rule of thumb: ${ROOM_WORD[roomType]} want satin or higher so splashes wipe off. Flat and matte hold stains here.`,
    };
  }
  if (sheenStep(sheen) >= sheenStep("semi-gloss")) {
    return {
      level: "info",
      text: `High sheen on walls shows every roller mark and drywall flaw. It's usually reserved for trim and doors.`,
    };
  }
  return null;
}

// --- Floor LRV ----------------------------------------------------------------

export function floorLrvTip(lrv: number): Tip | null {
  if (lrv < 20) {
    return {
      level: "info",
      text: `Very dark floors show dust, lint and footprints readily. Beautiful, but plan on more sweeping.`,
    };
  }
  if (lrv > 70) {
    return {
      level: "info",
      text: `Very light floors hide dust but show scuffs and pet traffic. Worth a tougher wear layer.`,
    };
  }
  return null;
}
